import { RoutePath } from 'config/routes';
import { PageId } from 'types/pageId';
import { AppData, Router } from '@lightningjs/sdk';
import {
  isEpisode,
  isMovie,
  isSeries,
  isVideo,
  isChannel,
  isLiveEvent,
} from 'support/contentUtils';
import {
  Channel,
  Episode,
  LiveEvent,
  Media,
  Show,
  Video,
} from 'types/api/media';
import { SelectItemContext } from 'types/events';
import { ContentHubSlugs } from 'types/contentHubs';
import { ContentHubPageParams } from 'components/pages/content-hub/ContentHubPage';
import { ErrorCause } from 'components/widgets/ErrorModal';
import { ContentHub } from 'services/cwData';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';

/**
 * Gets route path from page ID. Uses slug if the page ID is Content Hub
 * @param pageId The page ID we want to route to
 * @param slug Used for linking to Content Hub pages
 * @returns The route to the page ID provided
 */
export const getRoutePathFromPageId = (
  pageId: PageId,
  slug?: string,
): string => {
  switch (pageId) {
    case PageId.SPLASH:
      return RoutePath.SPLASH;
    case PageId.SEARCH:
      return RoutePath.SEARCH;
    case PageId.EPG:
      return RoutePath.EPG;
    case PageId.CONTENT_HUB:
      if (!slug) return RoutePath.ERROR;
      return `${RoutePath.CONTENT_HUB}/${slug}`;
    case PageId.SETTINGS:
      return RoutePath.SETTINGS;
    case PageId.MOVIE_DETAIL:
      return RoutePath.MOVIE_DETAIL;
    case PageId.SERIES_DETAIL:
      return RoutePath.SERIES_DETAIL;
    case PageId.LIVE_EVENT_DETAIL:
      return RoutePath.LIVE_EVENT_DETAIL;
    case PageId.PLAYBACK:
      return RoutePath.PLAY;
    case PageId.MY_STUFF:
      return RoutePath.MY_STUFF;
    case PageId.LOGIN:
      return RoutePath.LOGIN;
    case PageId.WATCHLIST:
      return RoutePath.WATCHLIST;
    case PageId.CONTINUE_WATCHING:
      return RoutePath.CONTINUE_WATCHING;
    case PageId.UNKNOWN:
    case PageId.ERROR:
    default:
      return RoutePath.ERROR;
  }
};

// Pages a brand can disable. App never registers a disabled page, so
// navigating to it falls through to RoutePath.NOT_FOUND.
export const isRouteDisabled = (path: string): boolean => {
  switch (path) {
    case RoutePath.LOGIN:
      return AppData?.loginRadiusService === null;
    default:
      return false;
  }
};

export const getPageIdFromHash = (hash: string): string => {
  const baseRoute = hash.split('/')[0];

  switch (baseRoute) {
    case RoutePath.ERROR:
      return PageId.ERROR;
    case RoutePath.SPLASH:
      return PageId.SPLASH;
    case RoutePath.SEARCH:
      return PageId.SEARCH;
    case RoutePath.EPG:
      return PageId.EPG;
    case RoutePath.CONTENT_HUB:
      return PageId.CONTENT_HUB;
    case RoutePath.SETTINGS:
      return PageId.SETTINGS;
    case RoutePath.MOVIE_DETAIL:
      return PageId.MOVIE_DETAIL;
    case RoutePath.SERIES_DETAIL:
      return PageId.SERIES_DETAIL;
    case RoutePath.LIVE_EVENT_DETAIL:
      return PageId.LIVE_EVENT_DETAIL;
    case RoutePath.PLAY:
      return PageId.PLAYBACK;
    case RoutePath.MY_STUFF:
      return PageId.MY_STUFF;
    case RoutePath.LOGIN:
      return PageId.LOGIN;
    case RoutePath.FAVORITE_CHANNELS:
      return PageId.FAVORITE_CHANNELS;
    case RoutePath.WATCHLIST:
      return PageId.WATCHLIST;
    case RoutePath.CONTINUE_WATCHING:
      return PageId.CONTINUE_WATCHING;
    default:
      return PageId.UNKNOWN;
  }
};

export const navigateToPlay = (
  guid: string,
  context: Pick<
    SelectItemContext,
    'viewContext' | 'pageId' | 'listItemContext'
  >,
  contentType: 'show' | 'live' = 'show',
  historyStore = true,
) => {
  Router.navigate(
    `${RoutePath.PLAY}/${contentType}/${guid}`,
    {
      viewContext: context.viewContext,
      listItemContext: context.listItemContext,
      fromPageId: context.pageId,
    },
    historyStore,
  );
};

/**
 * Navigates to content hub slug provided
 * @param hubSlug The hub we want to navigate to
 * @param params Additional parameters we want to pass to content hub
 */
export const navigateToContentHub = (
  hubSlug: string,
  params?: ContentHubPageParams,
) => {
  let path = `${RoutePath.CONTENT_HUB}/${hubSlug}`;
  if (params?.nested) path += '/true';

  Router.navigate(path, {
    ...params,
    hubSlug,
  });
};

/**
 * The home page is a content hub page
 * @param params Additional parameters we want to pass to home page
 */
export const navigateHome = (params?: ContentHubPageParams) => {
  navigateToContentHub(ContentHubSlugs.HOME, params);
};

export const onTileSelectedHandler = (
  data: Media | ContentHub,
  context: SelectItemContext = {},
) => {
  const { viewContext, listItemContext, pageId, action } = context;
  if (action === 'play' && viewContext && pageId) {
    // Assume data is of type Media
    data = data as Media;

    if (isEpisode(data) || isVideo(data)) {
      const id = (data as Episode).guid || '';
      navigateToPlay(id, { viewContext, listItemContext, pageId }, 'show');
    } else if (isMovie(data) || isSeries(data)) {
      const id = (data as Show).firstEpisode?.guid || '';
      navigateToPlay(id, { viewContext, listItemContext, pageId }, 'show');
    } else if (isLiveEvent(data)) {
      // handle live event separetely in its own helper function
      navigateToLiveEventDetailsPage((data as LiveEvent).slug, context);
    }
  } else if (action === 'details') {
    // Assume data is of type Media
    data = data as Media;

    if (isSeries(data)) {
      navigateToSeriesDetailsPage((data as Show).id, {
        listItemContext,
        pageId,
      });
    } else if (isMovie(data)) {
      navigateToMovieDetailsPage((data as Show).id, {
        listItemContext,
        pageId,
      });
    } else if (isLiveEvent(data)) {
      navigateToLiveEventDetailsPage((data as LiveEvent).slug, context);
    } else if (isChannel(data)) {
      navigateToChannel((data as Channel).slug, { viewContext });
    }
  } else if (action === 'navigation') {
    // Assume data is of type ContentHub
    data = data as ContentHub;

    navigateToContentHub(data.slug, { nested: true });
  }
};

export const navigateToSeriesDetailsPage = (
  slug: string,
  context: Pick<SelectItemContext, 'pageId' | 'listItemContext'>,
) => {
  Router.navigate(
    `${RoutePath.SERIES_DETAIL}/${slug}`,
    {
      listItemContext: context.listItemContext,
      fromPageId: context.pageId,
    },
    context.pageId !== PageId.SERIES_DETAIL,
  );
};

export const navigateToMovieDetailsPage = (
  slug: string,
  context: Pick<SelectItemContext, 'pageId' | 'listItemContext'>,
) => {
  Router.navigate(
    `${RoutePath.MOVIE_DETAIL}/${slug}`,
    {
      listItemContext: context.listItemContext,
      fromPageId: context.pageId,
    },
    context.pageId !== PageId.MOVIE_DETAIL,
  );
};

export const navigateToLiveEventDetailsPage = (
  slug: string,
  context: SelectItemContext,
) => {
  Router.navigate(
    `${RoutePath.LIVE_EVENT_DETAIL}/${slug}`,
    {
      fromPageId: context.pageId,
      selectItemContext: context,
    },
    context.pageId !== PageId.LIVE_EVENT_DETAIL,
  );
};

export const navigateToChannel = (
  slug?: string,
  params?: Router.PageParams,
) => {
  let route = `${RoutePath.EPG}`;
  if (slug) {
    route += `/${slug}`;
  }

  Router.navigate(route, { ...params });
};

export const getHomeHistoryEntry = (): Router.HistoryEntry => {
  return {
    hash: `${RoutePath.CONTENT_HUB}/${ContentHubSlugs.HOME}`,
    state: null,
  };
};

export const getMovieDetailsHistoryEntry = (
  slug: string,
): Router.HistoryEntry => {
  return {
    hash: `${RoutePath.MOVIE_DETAIL}/${slug}`,
    state: null,
  };
};

export const getSeriesDetailsHistoryEntry = (
  slug: string,
  season?: number,
): Router.HistoryEntry => {
  let hash = `${RoutePath.SERIES_DETAIL}/${slug}`;
  if (season !== undefined) hash += `/${season}`;

  return {
    hash: hash,
    state: null,
  };
};

export const getVodPlaybackHistoryEntry = (
  guid: string,
): Router.HistoryEntry => {
  return {
    hash: `${RoutePath.PLAY}/show/${guid}`,
    state: null,
  };
};

export const getLivePlaybackHistoryEntry = (
  slug?: string,
): Router.HistoryEntry => {
  let hash = `${RoutePath.PLAY}/live`;
  if (slug !== undefined) hash += `/${slug}`;

  return {
    hash,
    state: null,
  };
};

/**
 * Returns history entry for live event
 *
 * @param slug Live event slug
 * @returns Live event details page history entry if slug provided. Otherwise,
 * shows default live page history entry (from config)
 */
export const getLiveEventHistoryEntry = (slug: string): Router.HistoryEntry => {
  return {
    hash: `${RoutePath.LIVE_EVENT_DETAIL}/${slug}`,
    state: null,
  };
};

export const getCurrentHistoryEntry = (): Router.HistoryEntry | null => {
  const hash = Router.getActiveHash();
  if (!hash) return null;

  return {
    hash,
    state: Router.getHistoryState(),
  };
};

export const navigateDetailsFromVideo = (mediaItem: Video) => {
  const { showSlug } = mediaItem;
  const history = Router.getHistory();
  const lastHistoryEntry = history[history.length - 1];

  if (
    lastHistoryEntry &&
    (lastHistoryEntry.hash.startsWith(RoutePath.SERIES_DETAIL) ||
      lastHistoryEntry.hash.startsWith(RoutePath.MOVIE_DETAIL))
  ) {
    // Remove the last history entry if it's a series or movie details page
    history.pop();
  }

  if (isSeries(mediaItem)) {
    const seriesDetailsHistoryEntry = getSeriesDetailsHistoryEntry(showSlug);
    history.push(seriesDetailsHistoryEntry);
    Router.setHistory(history);
  } else if (isMovie(mediaItem)) {
    const movieDetailsHistoryEntry = getMovieDetailsHistoryEntry(showSlug);
    history.push(movieDetailsHistoryEntry);
    Router.setHistory(history);
  }
  Router.back();
};

export const navigateToContentUnavailableError = () => {
  Router.navigate(RoutePath.ERROR, { cause: ErrorCause.UNAVAILABLE });
};

/*
  This triggers the unavailable error modal whenever there is no content to handle deep link errors + general errors.
  Otherwise it will return the content/result of the first parameter (i.e. asyncFunc).
*/
export const getContentWrapper = async <T = unknown>(
  asyncFunc: () => Promise<T>,
  navigateToErrorPage = true,
  handleUndefinedAsError = true,
): Promise<T | undefined> => {
  try {
    const result = await asyncFunc();
    if (handleUndefinedAsError && result === undefined) {
      // If the result is undefined and handleUndefinedAsError is true, handle as an error
      throw new Error('Content is undefined');
    }
    return result;
  } catch (error) {
    reportError({ error: error, userFacing: true, code: ErrorCode.c32 });
    if (navigateToErrorPage) navigateToContentUnavailableError();
    return undefined;
  }
};

export const shouldShowLogin = () => {
  const loginRegistrationShownTimestamp =
    AppData!.storageService.loginRegistrationShownTimestamp.get();

  const showLogin =
    AppData!.loginRadiusService !== null &&
    AppData!.config?.login.landing_page.is_visible &&
    !loginRegistrationShownTimestamp;

  return showLogin;
};
